/**
 * The vault backup: one file holding every save the player has.
 *
 * This is the saves-only profile of the `.rrdata` container defined in
 * docs/design/global-save-sharing-plan.md — a plain zip with a `manifest.json`
 * at the root, written with the same codec the save editor and slot exporter
 * use. It carries no game data: that is enormous, incompressible (measured:
 * master.dat zstd 98.4%), and replaceable, whereas a save is 1MB and gone
 * forever. A distinct extension keeps it out of the file pickers that want
 * .zip, and marks it as ours for the importer, which recognises it by the
 * manifest rather than by the name on the end.
 *
 * Restore never writes into a live slot. Restored saves land in the vault's
 * `restored` store and the player promotes them; see store.ts.
 */

import { activeGameType } from './config';
import { getMeta, putRestored, type InstallMeta, type RestoredSave } from './store';
import { parseSaveHeader, readPersistedSaveFiles, safeArchiveParts } from './saves';
import { createZip, type ZipInput } from './zip/write';
import { readCentralDirectory, verifiedEntryBytes } from './zip/read';

export const VAULT_EXTENSION = '.rrdata';
const MANIFEST_NAME = 'manifest.json';
const SAVES_PREFIX = 'saves/';
const KIND = 'vault-backup';
const SCHEMA_VERSION = 1;

/** Restore limits. A vault of real saves is a few MB; these are bomb guards. */
const MAX_ENTRIES = 8192;
const MAX_FILE_BYTES = 64 * 1024 * 1024;
const MAX_TOTAL_BYTES = 256 * 1024 * 1024;

export interface VaultSaveEntry {
  dir: string;
  slot: number | null;
  characterName: string;
  description: string;
  mapFile: string;
  version: string;
  savedAt: string | null;
  fileCount: number;
  bytes: number;
  /**
   * The save's modlist binding, when one exists. Nothing in the shell writes
   * `slot.mods.json` yet — the sharing plan introduces it as provenance
   * metadata — so this is null for every save taken today, and the field exists
   * so that a vault written next year still restores into this reader.
   */
  mods: unknown;
}

export interface VaultManifest {
  kind: typeof KIND;
  schemaVersion: number;
  created: string;
  app: string;
  engine: { module: string; sha256: string | null };
  install: {
    source: InstallMeta['source'] | null;
    hasPatch: boolean | null;
    dats: string[];
    fileCount: number | null;
    totalBytes: number | null;
    /** See installFingerprint(): a fingerprint of the install *record*. */
    fingerprint: string | null;
  };
  saves: VaultSaveEntry[];
}

export interface VaultBackup {
  blob: Blob;
  filename: string;
  manifest: VaultManifest;
  saveCount: number;
  fileCount: number;
}

/**
 * Identifies the installation a save was made against.
 *
 * Deliberately a digest of the install *record* — source, dat names, file and
 * byte counts — and not of the 554MB of game data itself, which we would have
 * to read end to end to hash. It is enough to notice "this backup came from a
 * different install" and honest about being no more than that; the sharing
 * plan's corpus fingerprint is a separate, heavier thing.
 */
async function installFingerprint(meta: InstallMeta | undefined): Promise<string | null> {
  if (!meta || !crypto?.subtle) return null;
  const canonical = JSON.stringify({
    source: meta.source,
    dats: [...meta.dats].sort(),
    hasPatch: meta.hasPatch,
    fileCount: meta.fileCount,
    totalBytes: meta.totalBytes,
  });
  try {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

const slotNumber = (dir: string): number | null => {
  const match = /^SLOT(\d+)$/i.exec(dir);
  return match ? Number(match[1]) : null;
};

/** Groups SAVEGAME-relative files by their top-level directory. */
function byDirectory(files: ZipInput[]): Map<string, ZipInput[]> {
  const groups = new Map<string, ZipInput[]>();
  for (const file of files) {
    const dir = file.name.split('/')[0];
    const existing = groups.get(dir);
    if (existing) existing.push(file);
    else groups.set(dir, [file]);
  }
  return groups;
}

/**
 * Reads the whole SAVEGAME tree, engine or no engine.
 *
 * Everything durable is taken, not just SLOT dirs: an editor backup or an
 * archive folder that a later version writes alongside them is exactly the sort
 * of thing a backup must not quietly omit. The only exclusions are the slot
 * swapper's own scratch directories, which are transactional by definition.
 */
async function backupSourceFiles(): Promise<ZipInput[]> {
  return readPersistedSaveFiles((path) => !path.startsWith('.'));
}

export async function buildVaultBackup(): Promise<VaultBackup> {
  const files = await backupSourceFiles();
  if (!files.length) throw new Error('There are no saves to back up yet.');
  const install = await getMeta<InstallMeta>('install').catch(() => undefined);

  const saves: VaultSaveEntry[] = [];
  for (const [dir, group] of byDirectory(files)) {
    const saveDat = group.find((file) => file.name.toUpperCase() === `${dir.toUpperCase()}/SAVE.DAT`);
    let header = null;
    if (saveDat) {
      try {
        header = parseSaveHeader(saveDat.data);
      } catch {
        // An unreadable SAVE.DAT is still worth backing up — it is the player's
        // only copy of whatever it is — so it goes in the container unparsed.
      }
    }
    const mods = group.find((file) => file.name.toLowerCase() === `${dir.toLowerCase()}/slot.mods.json`);
    saves.push({
      dir,
      slot: slotNumber(dir),
      characterName: header?.characterName ?? '',
      description: header?.description ?? '',
      mapFile: header?.mapFile ?? '',
      version: header?.version ?? '',
      savedAt: header?.savedAt ? header.savedAt.toISOString() : null,
      fileCount: group.length,
      bytes: group.reduce((n, file) => n + file.data.length, 0),
      mods: mods ? safeJson(new TextDecoder().decode(mods.data)) : null,
    });
  }

  const manifest: VaultManifest = {
    kind: KIND,
    schemaVersion: SCHEMA_VERSION,
    created: new Date().toISOString(),
    app: 'Rerun Reactor',
    engine: {
      // The shell loads the engine from a fixed path and is never told which
      // build it got, so the identifier is the path and the hash is honestly
      // null. Vendoring stamps a version one day; the field is here for it.
      module: activeGameType() === 'fo1' ? '/engine/fallout1-ce.js.js' : '/engine/fallout2-ce.js.js',
      sha256: null,
    },
    install: {
      source: install?.source ?? null,
      hasPatch: install?.hasPatch ?? null,
      dats: install?.dats ?? [],
      fileCount: install?.fileCount ?? null,
      totalBytes: install?.totalBytes ?? null,
      fingerprint: await installFingerprint(install),
    },
    saves: saves.sort((a, b) => a.dir.localeCompare(b.dir)),
  };

  const inputs: ZipInput[] = [
    { name: MANIFEST_NAME, data: new TextEncoder().encode(JSON.stringify(manifest, null, 2)) },
    ...files.map((file) => ({ ...file, name: `${SAVES_PREFIX}${file.name}` })),
  ];
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    blob: await createZip(inputs),
    filename: `rerun-vault-${stamp}${VAULT_EXTENSION}`,
    manifest,
    saveCount: saves.length,
    fileCount: files.length,
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

// --- reading one back --------------------------------------------------------

/**
 * Is this a vault backup? Answered from the manifest inside, never from the
 * extension: a player who renames the file, or whose mail client helpfully
 * appends .zip, still gets their saves back.
 */
export async function readVaultManifest(file: Blob): Promise<VaultManifest | null> {
  let entries;
  try {
    entries = await readCentralDirectory(file);
  } catch {
    return null;
  }
  const manifestEntry = entries.find((entry) => !entry.isDirectory && entry.name === MANIFEST_NAME);
  if (!manifestEntry) return null;
  try {
    const bytes = await verifiedEntryBytes(file, manifestEntry, 1024 * 1024);
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as VaultManifest;
    if (parsed?.kind !== KIND) return null;
    return parsed;
  } catch {
    return null;
  }
}

export interface RestoreSummary {
  restored: number;
  files: number;
  manifest: VaultManifest;
}

/**
 * Unpacks a vault backup into the restore archive.
 *
 * Nothing here touches SAVEGAME. Every save arrives as a pending item the
 * player promotes into a slot of their choosing, which is what makes "restore"
 * safe to offer to someone who is mid-playthrough and only wanted one old save
 * back.
 */
export async function restoreVaultBackup(file: File): Promise<RestoreSummary> {
  const manifest = await readVaultManifest(file);
  if (!manifest) throw new Error('That file is not a Rerun vault backup.');
  if (manifest.schemaVersion > SCHEMA_VERSION) {
    throw new Error(
      `That backup was written by a newer version of Rerun Reactor (format ${manifest.schemaVersion}). Update the page and try again.`
    );
  }

  const entries = (await readCentralDirectory(file)).filter(
    (entry) => !entry.isDirectory && entry.name.startsWith(SAVES_PREFIX)
  );
  if (!entries.length) throw new Error('That backup contains no saves.');
  if (entries.length > MAX_ENTRIES) throw new Error(`Backup has too many files (${entries.length}; limit ${MAX_ENTRIES}).`);

  const groups = new Map<string, { path: string; blob: Blob }[]>();
  let total = 0;
  for (const entry of entries) {
    const parts = safeArchiveParts(entry.name).slice(1);
    if (parts.length < 2) continue; // a loose file with no save directory
    total += entry.uncompressedSize;
    if (!Number.isSafeInteger(total) || total > MAX_TOTAL_BYTES) {
      throw new Error(`Backup expands beyond the ${MAX_TOTAL_BYTES / 1024 / 1024} MiB limit.`);
    }
    const dir = parts[0];
    const bytes = await verifiedEntryBytes(file, entry, MAX_FILE_BYTES);
    const group = groups.get(dir) ?? [];
    group.push({ path: parts.slice(1).join('/'), blob: new Blob([bytes as unknown as BlobPart]) });
    groups.set(dir, group);
  }

  const restoredAt = Date.now();
  const saves: RestoredSave[] = [];
  for (const [dir, group] of groups) {
    const described = manifest.saves.find((save) => save.dir === dir);
    const savedAt = described?.savedAt ? Date.parse(described.savedAt) : NaN;
    saves.push({
      id: `${restoredAt.toString(36)}-${dir.toLowerCase()}-${Math.random().toString(16).slice(2, 8)}`,
      origin: `${file.name} → ${dir}`,
      restoredAt,
      characterName: described?.characterName ?? '',
      description: described?.description ?? '',
      mapFile: described?.mapFile ?? '',
      savedAt: Number.isFinite(savedAt) ? savedAt : null,
      bytes: group.reduce((n, item) => n + item.blob.size, 0),
      files: group,
    });
  }
  await putRestored(saves);
  return { restored: saves.length, files: entries.length, manifest };
}

// --- how far behind are we? --------------------------------------------------

const KEY_DIGESTS = 'rr.durability.backedUpSlots';

/** A slot's shape, cheap enough to recompute on every archive render. */
const digestOf = (dir: string, group: ZipInput[]): string =>
  [
    dir.toUpperCase(),
    group.length,
    group.reduce((n, file) => n + file.data.length, 0),
    Math.max(...group.map((file) => file.lastModified ?? 0)),
  ].join(':');

function readDigests(): string[] {
  try {
    return JSON.parse(localStorage.getItem(KEY_DIGESTS) ?? '[]') as string[];
  } catch {
    return [];
  }
}

function writeDigests(digests: string[]): void {
  try {
    localStorage.setItem(KEY_DIGESTS, JSON.stringify(digests));
  } catch {
    /* the nudge degrades to "back it up again"; nothing breaks */
  }
}

/**
 * How many saves have changed since the last backup.
 *
 * Counted by comparing per-slot shapes rather than by tallying save events: a
 * counter drifts the moment a save is deleted or a backup is taken in another
 * tab, and this cannot.
 */
export async function unbackedSaveCount(): Promise<number> {
  const known = new Set(readDigests());
  if (!known.size) {
    const files = await backupSourceFiles();
    return byDirectory(files).size;
  }
  const files = await backupSourceFiles();
  let count = 0;
  for (const [dir, group] of byDirectory(files)) {
    if (!known.has(digestOf(dir, group))) count++;
  }
  return count;
}

/** Records the state a backup just captured, so the nudge goes quiet. */
export async function markBackedUp(): Promise<void> {
  const files = await backupSourceFiles();
  writeDigests([...byDirectory(files)].map(([dir, group]) => digestOf(dir, group)));
}
